// ============================================================================
// SUPPORT TICKETS — data layer (real FastAPI + MongoDB backend, Phase 6)
// ----------------------------------------------------------------------------
// Read = view_support; write = edit_support. Store backend-hydrated + mutazioni
// ottimistiche (API sincrona invariata → i moduli non cambiano). Il modulo
// pubblico (form anonimo) usa endpoint pubblici async. Auto-match cliente +
// assegnazione + email log avvengono lato server. NO email/upload/n8n reali.
// ============================================================================

import { listTeam } from "@/lib/teamData";
import { getActiveRegistryClients } from "@/lib/clientRegistry";
import { MONTHS_IT } from "@/lib/tasksData";
import { api } from "@/lib/api";

export { MONTHS_IT };

const now = () => new Date().toISOString();
const uid = (p) => `${p}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
const slugify = (s) => (s || "form").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export const assignableUsers = () => listTeam().filter((u) => u.role !== "client_user").map((u) => ({ id: u.id, name: u.name, initials: u.initials, avatar: u.avatar }));
export const userName = (id) => listTeam().find((u) => u.id === id)?.name || null;

export const FIELD_TYPES = {
  text: "Testo breve", textarea: "Testo lungo", email: "Email", phone: "Telefono",
  dropdown: "Menu a tendina", checkbox: "Checkbox", multiple_choice: "Scelta multipla",
  date: "Data", file: "Allega file", link: "Link / URL", approval: "Checkbox approvazione",
};
export const FIELD_WIDTHS = { full: "Larghezza piena", half: "Metà larghezza" };
export const PRIORITIES = {
  low: { label: "Bassa", tone: "neutral" }, normal: { label: "Normale", tone: "info" },
  high: { label: "Alta", tone: "warning" }, urgent: { label: "Urgente", tone: "error" },
};
export const TICKET_STATUS = {
  new: { label: "Nuova", tone: "info" }, in_progress: { label: "In corso", tone: "warning" }, completed: { label: "Completata", tone: "ok" },
};
export const REQUEST_TYPES = ["Piano editoriale", "Modifica sito", "Modifica campagna", "Problema tecnico", "Richiesta generica"];
export const ASSIGN_TYPES = {
  specific_user: "Assegna a membro specifico", client_assigned_member: "Assegna al referente del cliente",
  dropdown_value: "Assegna in base a valore dropdown", unassigned: "Lascia non assegnato",
};
export const MAPPING_STATUS = {
  matched: { label: "Abbinato", tone: "ok" }, unmatched: { label: "Non abbinato", tone: "error" }, manually_assigned: { label: "Assegnato manualmente", tone: "info" }, internal: { label: "Interno", tone: "neutral" },
};
export const EMAIL_TEMPLATES = {
  ticket_received: { label: "Ticket ricevuto", subject: "Abbiamo ricevuto la tua richiesta", body: "Ciao {{client_name}}, abbiamo ricevuto la tua richiesta “{{ticket_subject}}”. Ti aggiorneremo a breve." },
  taken_in_charge: { label: "Ticket preso in carico", subject: "La tua richiesta di supporto è stata presa in carico", body: "Ciao {{client_name}}, la tua richiesta “{{ticket_subject}}” è stata presa in carico dal nostro team." },
  completed: { label: "Ticket completato", subject: "La tua richiesta di supporto è stata completata", body: "Ciao {{client_name}}, la tua richiesta “{{ticket_subject}}” è stata completata." },
  reopened: { label: "Ticket riaperto", subject: "La tua richiesta di supporto è stata riaperta", body: "Ciao {{client_name}}, la tua richiesta “{{ticket_subject}}” è stata riaperta ed è di nuovo in lavorazione." },
};

// ---------------------------------------------------------------------------
// CACHE (backend-hydrated)
// ---------------------------------------------------------------------------
const empty = () => ({ forms: [], tickets: [], history: [], emailLogs: [], comments: [] });
let _store = empty();
let _hydrated = false;
const _subs = new Set();
const notify = () => _subs.forEach((fn) => { try { fn(); } catch { /* noop */ } });
export const subscribeSupport = (fn) => { _subs.add(fn); return () => _subs.delete(fn); };
export const isSupportReady = () => _hydrated;

export async function hydrateSupport() {
  try {
    const { data } = await api.get("/support/store", { params: { include_archived: true } });
    _store = { ...empty(), ...data };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("hydrateSupport failed", e?.response?.status);
  } finally { _hydrated = true; notify(); }
}
export const resetSupport = () => { _store = empty(); _hydrated = false; };
const resync = () => { hydrateSupport(); };
const read = () => _store;
const touch = () => { _store = { ..._store }; notify(); };
const persist = (promise) => { promise.catch(resync); };

// ---------------------------------------------------------------------------
// PUBLIC (anonymous form) — async
// ---------------------------------------------------------------------------
export const publicGetForm = async (token) => {
  try { const { data } = await api.get(`/support/public/form/${token}`); return data; }
  catch { return null; }
};
export const publicSubmitTicket = async (token, payload) => {
  const { data } = await api.post("/support/public/submit", { token, payload });
  return data;
};

// ---------------------------------------------------------------------------
// FORMS
// ---------------------------------------------------------------------------
export const listForms = () => read().forms;
export const getForm = (id) => read().forms.find((f) => f.id === id) || null;
export const saveForm = (form) => {
  const withMeta = { ...form, form_slug: slugify(form.form_name), updated_at: now() };
  if (form.id && _store.forms.some((f) => f.id === form.id)) {
    _store.forms = _store.forms.map((f) => (f.id === form.id ? withMeta : f)); touch();
    persist(api.post("/support/forms", withMeta));
    return;
  }
  const id = form.id || uid("form");
  const { form_token: _tok, ...rest } = withMeta;
  const doc = { ...rest, id, created_at: now() };
  _store.forms = [..._store.forms, doc]; touch();
  persist(api.post("/support/forms", doc).then(({ data }) => { _store.forms = _store.forms.map((f) => (f.id === id ? data : f)); touch(); }));
};
export const createManualTicket = async (body) => {
  const { data } = await api.post("/support/tickets", body);
  _store.tickets = [data, ..._store.tickets]; touch();
  return data;
};
export const createDefaultForm = () => ({
  ...createEmptyForm(), form_name: "Richiesta di assistenza", visibility: "portal", is_default: true,
  description: "Apri una richiesta di assistenza al nostro team.",
  fields: [
    { id: uid("fld"), field_label: "Oggetto", field_name: "request_subject", field_type: "text", is_required: true, placeholder: "", help_text: "", width: "full", options: [], sort_order: 0 },
    { id: uid("fld"), field_label: "Descrizione", field_name: "description", field_type: "textarea", is_required: true, placeholder: "", help_text: "", width: "full", options: [], sort_order: 1 },
  ],
});
export const createEmptyForm = () => ({
  id: uid("form"), form_name: "Nuovo modulo di supporto", form_slug: "nuovo-modulo",
  description: "", is_active: true,
  created_by: "u_andrea", created_at: now(), updated_at: now(),
  fields: [
    { id: uid("fld"), field_label: "Ragione Sociale", field_name: "company_name", field_type: "text", is_required: true, help_text: "Serve per abbinare la richiesta al cliente.", placeholder: "", width: "half", options: [], sort_order: 0 },
    { id: uid("fld"), field_label: "Nome e cognome", field_name: "requester_name", field_type: "text", is_required: true, placeholder: "", help_text: "", width: "half", options: [], sort_order: 1 },
    { id: uid("fld"), field_label: "Email", field_name: "requester_email", field_type: "email", is_required: true, placeholder: "", help_text: "", width: "half", options: [], sort_order: 2 },
    { id: uid("fld"), field_label: "Oggetto", field_name: "request_subject", field_type: "text", is_required: true, placeholder: "", help_text: "", width: "half", options: [], sort_order: 3 },
  ],
  assignment: { type: "unassigned", user_id: null, user_name: null, dropdown_field_name: null, rules: [] },
});
export const duplicateForm = (id) => {
  const f = getForm(id); if (!f) return;
  const nid = uid("form");
  const copy = { ...JSON.parse(JSON.stringify(f)), id: nid, form_name: `${f.form_name} (copia)`, form_token: `mock-token-${Math.floor(Math.random() * 900 + 100)}`, created_at: now(), updated_at: now() };
  _store.forms = [..._store.forms, copy]; touch();
  persist(api.post(`/support/forms/${id}/duplicate`).then(({ data }) => { _store.forms = _store.forms.map((x) => (x.id === nid ? data : x)); touch(); }));
};
export const toggleFormActive = (id) => { _store.forms = _store.forms.map((f) => (f.id === id ? { ...f, is_active: !f.is_active } : f)); touch(); persist(api.post(`/support/forms/${id}/toggle`)); };
export const removeForm = (id) => { _store.forms = _store.forms.filter((f) => f.id !== id); touch(); persist(api.delete(`/support/forms/${id}`)); };
export const newFieldId = () => uid("fld");
export const newRuleId = () => uid("rule");

// ---------------------------------------------------------------------------
// TICKETS
// ---------------------------------------------------------------------------
export const listTickets = () => read().tickets.filter((t) => !t.archived_at).slice().sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at));
export const listArchivedTickets = () => read().tickets.filter((t) => !!t.archived_at).slice().sort((a, b) => new Date(b.archived_at) - new Date(a.archived_at));
export const getTicket = (id) => read().tickets.find((t) => t.id === id) || null;
export const ticketsByStatus = (status, list) => (list || listTickets()).filter((t) => t.status === status);
export const ticketsByClient = (clientId) => listTickets().filter((t) => t.client_id === clientId);

export const renderTemplate = (type, ticket) => {
  const tpl = EMAIL_TEMPLATES[type]; if (!tpl) return "";
  const clientName = ticket.company_name || ticket.requester_name || "cliente";
  return tpl.body.replace(/\{\{client_name\}\}/g, clientName).replace(/\{\{ticket_subject\}\}/g, ticket.request_subject || "");
};

export const updateTicket = (id, patch) => { _store.tickets = _store.tickets.map((t) => (t.id === id ? { ...t, ...patch, updated_at: now() } : t)); touch(); persist(api.patch(`/support/tickets/${id}`, patch)); };
export const assignTicket = (id, userId) => {
  _store.tickets = _store.tickets.map((t) => {
    if (t.id !== id) return t;
    const wasUnmatched = t.mapping_status === "unmatched";
    return { ...t, assigned_to_user_id: userId || null, assigned_to_user_name: userName(userId), mapping_status: userId && wasUnmatched ? "manually_assigned" : t.mapping_status, updated_at: now() };
  }); touch();
  persist(api.post(`/support/tickets/${id}/assign`, { user_id: userId || null }));
};
export const changeTicketStatus = (id, newStatus) => {
  const t = _store.tickets.find((x) => x.id === id); if (!t || t.status === newStatus) return;
  const prev = t.status;
  const patch = { status: newStatus, updated_at: now() };
  if (newStatus === "in_progress") { patch.taken_in_charge_at = t.taken_in_charge_at || now(); if (prev === "completed") patch.completed_at = null; }
  if (newStatus === "completed") patch.completed_at = now();
  _store.tickets = _store.tickets.map((x) => (x.id === id ? { ...x, ...patch } : x)); touch();
  persist(api.post(`/support/tickets/${id}/status`, { status: newStatus }).then(() => resync()));
};

export const archiveTicket = (id) => {
  _store.tickets = _store.tickets.map((t) => (t.id === id ? { ...t, archived_at: now() } : t)); touch();
  persist(api.post(`/support/tickets/${id}/archive`));
};
export const unarchiveTicket = (id) => {
  _store.tickets = _store.tickets.map((t) => (t.id === id ? { ...t, archived_at: null } : t)); touch();
  persist(api.post(`/support/tickets/${id}/unarchive`));
};
export const removeTicket = (id) => {
  _store.tickets = _store.tickets.filter((t) => t.id !== id);
  _store.history = _store.history.filter((h) => h.ticket_id !== id);
  _store.emailLogs = _store.emailLogs.filter((e) => e.ticket_id !== id);
  _store.comments = _store.comments.filter((c) => c.ticket_id !== id);
  touch();
  persist(api.delete(`/support/tickets/${id}`));
};

export const bulkArchiveTickets = (ids) => {
  const set = new Set(ids);
  _store.tickets = _store.tickets.map((t) => (set.has(t.id) && !t.archived_at ? { ...t, archived_at: now() } : t)); touch();
  persist(api.post("/support/tickets/bulk/archive", { ids }));
};
export const bulkUnarchiveTickets = (ids) => {
  const set = new Set(ids);
  _store.tickets = _store.tickets.map((t) => (set.has(t.id) && t.archived_at ? { ...t, archived_at: null } : t)); touch();
  persist(api.post("/support/tickets/bulk/unarchive", { ids }));
};

// Impostazioni supporto (auto-archivio dei ticket completati).
export const getSupportSettings = async () => {
  try { const { data } = await api.get("/support/settings"); return data; }
  catch { return { auto_archive_completed_days: null }; }
};
export const saveSupportSettings = async (patch) => {
  const { data } = await api.patch("/support/settings", patch);
  resync();
  return data;
};

// Filtri salvati (per utente, backend-persistiti).
export const listSavedFilters = async () => {
  try { const { data } = await api.get("/support/saved-filters"); return data; }
  catch { return []; }
};
export const createSavedFilter = async (name, filters) => {
  const { data } = await api.post("/support/saved-filters", { name, filters });
  return data;
};
export const deleteSavedFilter = async (id) => { await api.delete(`/support/saved-filters/${id}`); };

export const ticketHistory = (id) => read().history.filter((h) => h.ticket_id === id).sort((a, b) => new Date(a.changed_at) - new Date(b.changed_at));
export const ticketEmails = (id) => read().emailLogs.filter((e) => e.ticket_id === id).sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at));
export const ticketComments = (id) => read().comments.filter((c) => c.ticket_id === id).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
export const addComment = ({ ticketId, type, comment }) => {
  const local = { id: uid("cmt"), ticket_id: ticketId, comment_type: type || "internal_note", comment, created_by_user_id: "me", created_at: now() };
  _store.comments = [..._store.comments, local]; touch();
  persist(api.post(`/support/tickets/${ticketId}/comment`, { type, comment }).then(({ data }) => { _store.comments = _store.comments.map((c) => (c.id === local.id ? data : c)); touch(); }));
};

// ---------------------------------------------------------------------------
// KPI / OVERVIEW
// ---------------------------------------------------------------------------
export const supportKpis = () => {
  const t = listTickets();
  const completed = t.filter((x) => x.status === "completed");
  const resTimes = completed.filter((x) => x.completed_at && x.submitted_at).map((x) => (new Date(x.completed_at) - new Date(x.submitted_at)) / 36e5);
  const avgRes = resTimes.length ? resTimes.reduce((a, b) => a + b, 0) / resTimes.length : 0;
  return {
    newCount: t.filter((x) => x.status === "new").length,
    inProgress: t.filter((x) => x.status === "in_progress").length,
    completed: completed.length,
    highPriority: t.filter((x) => ["high", "urgent"].includes(x.priority) && x.status !== "completed").length,
    avgResolutionH: avgRes,
    totalHours: t.reduce((a, x) => a + (x.hours_logged || 0), 0),
  };
};

const groupCount = (arr, keyFn) => arr.reduce((acc, x) => { const k = keyFn(x) || "—"; acc[k] = (acc[k] || 0) + 1; return acc; }, {});
export const supportBreakdowns = () => {
  const t = listTickets();
  return {
    byPriority: groupCount(t, (x) => PRIORITIES[x.priority]?.label),
    byAssignee: groupCount(t, (x) => x.assigned_to_user_name || "Non assegnato"),
    byClient: groupCount(t, (x) => x.company_name),
    byType: groupCount(t, (x) => x.request_type),
  };
};

export const clientTicketSummary = (clientId) => {
  const t = ticketsByClient(clientId);
  return {
    total: t.length,
    newCount: t.filter((x) => x.status === "new").length,
    inProgress: t.filter((x) => x.status === "in_progress").length,
    completed: t.filter((x) => x.status === "completed").length,
    totalHours: t.reduce((a, x) => a + (x.hours_logged || 0), 0),
    latest: t.slice(0, 5),
  };
};

export const clientOptions = () => getActiveRegistryClients().map((c) => ({ id: c.id, name: c.client_name }));
export const publicFormUrl = (token) => `/support-form/${token}`;

// Clienti preferiti (Client History) — localStorage only.
const FAV_KEY = "aesthetic-os-support-fav-clients";
export const listFavClients = () => { try { return JSON.parse(localStorage.getItem(FAV_KEY) || "[]"); } catch { return []; } };
export const isFavClient = (id) => listFavClients().includes(id);
export const toggleFavClient = (id) => { const f = listFavClients(); const next = f.includes(id) ? f.filter((x) => x !== id) : [...f, id]; localStorage.setItem(FAV_KEY, JSON.stringify(next)); return next; };

// ---------------------------------------------------------------------------
// AVVISI TASK ASSEGNATI (banner + pallino topbar)
// ---------------------------------------------------------------------------
const SEV_RANK = { red: 0, orange: 1, green: 2 };

// Rosso: priorità Alta/Urgente o scaduto · Arancione: scade entro 24h o priorità Normale · Verde: ok.
export const ticketSeverity = (t) => {
  const nowMs = Date.now();
  const due = t.due_date ? new Date(`${String(t.due_date).slice(0, 10)}T23:59:59`).getTime() : null;
  if (due && due < nowMs) return "red";
  if (t.priority === "high" || t.priority === "urgent") return "red";
  if ((due && due - nowMs <= 24 * 36e5) || t.priority === "normal") return "orange";
  return "green";
};
export const worstSeverity = (tickets) =>
  tickets.reduce((w, t) => (SEV_RANK[ticketSeverity(t)] < SEV_RANK[w] ? ticketSeverity(t) : w), "green");

// Ticket assegnati all'utente e non ancora presi in carico (stato "Nuova").
export const myPendingTickets = (userId) =>
  userId ? listTickets().filter((t) => t.assigned_to_user_id === userId && t.status === "new") : [];

// Dismissal persistente per-task: la X nasconde il banner finché il task non
// viene completato o riassegnato (la chiave include l'assegnatario corrente).
const BANNER_DISMISS_KEY = "aesthetic-os-task-banner-dismissed";
const readDismissed = () => { try { return JSON.parse(localStorage.getItem(BANNER_DISMISS_KEY) || "{}"); } catch { return {}; } };
export const isTaskBannerDismissed = (t, userId) => readDismissed()[t.id] === `${userId || ""}:${t.assigned_to_user_id || ""}`;
export const dismissTaskBanner = (t, userId) => {
  const map = readDismissed();
  map[t.id] = `${userId || ""}:${t.assigned_to_user_id || ""}`;
  localStorage.setItem(BANNER_DISMISS_KEY, JSON.stringify(map));
};
